import React, { useEffect } from 'react';
import styles from '../ReadStats.module.scss';
import { Info } from 'new/components/monitor/components/Info';
import { Typography } from '@mui/material';
import moment from 'moment';
import { useResourceMetrics } from 'new/hooks/adapters/useResourceMetrics';
import { Stack } from 'new/elements/Stack';
import { fetchResourceFileStats } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_RESOURCE_FILE_STATS_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { RefreshRoundedIconButton } from 'new/elements/Button/RefreshRoundedIconButton';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import { clearFileDetails } from 'js/actions/metrics/fileDetails';

interface ReadStatsProps {
  resourceType: string;
  resourceId: string;
  useFiles: boolean;
  requestOnMount?: boolean;
  requestFiles?: (page: number) => void;
  mode?: string;
  isAdaptiveFlow?: boolean;
}

export const FilesStats = (props: ReadStatsProps) => {
  const { resourceId, resourceType, useFiles, requestOnMount, requestFiles, isAdaptiveFlow } =
    props;
  const {
    numFiles,
    numCompletedFiles,
    partialSuccessFiles,
    notStartedFiles,
    numErrorFiles,
    lastUpdated,
  } = useResourceMetrics(resourceType, resourceId);
  const dispatch = useAppDispatch();
  const loading = useIsLoading(GET_RESOURCE_FILE_STATS_REQUEST);
  const onRefresh = () => {
    requestFiles(1);
    dispatch(fetchResourceFileStats(resourceId, resourceType));
  };

  useEffect(() => {
    if (requestOnMount) {
      onRefresh();
    }

    return () => {
      /**
       * clear the file details cache when the component unmounts.
       */
      dispatch(clearFileDetails());
    };
  }, []);

  if (!useFiles) {
    return null;
  }

  let titleLabel;
  if (props?.mode === FILE_LIST_MODE_TYPES.MODE_WRITE) {
    titleLabel = 'File Write Stats';
  } else if (props?.mode === FILE_LIST_MODE_TYPES.MODE_READ && isAdaptiveFlow) {
    titleLabel = 'Trigger Task Stats';
  } else if (props?.mode === FILE_LIST_MODE_TYPES.MODE_READ) {
    titleLabel = 'File Read Stats';
  }

  const numInProgressFiles =
    numFiles - (numCompletedFiles + numErrorFiles + notStartedFiles + partialSuccessFiles);

  return (
    <div className={styles.filesStatsContainer}>
      <Stack direction="row" alignItems="center" padding="8px 20px" justifyContent="space-between">
        <Typography variant="body1" fontWeight="500">
          {titleLabel}
        </Typography>

        <Stack direction="row" alignItems="center" gap="8px">
          <Typography>
            Last updated on {moment(lastUpdated).format('MMM D, YYYY [•] h:mmA')}
          </Typography>
          <RefreshRoundedIconButton isLoading={loading} onRefreshClicked={onRefresh} />
        </Stack>
      </Stack>
      <div className={styles.metricsInfoContainer}>
        <div className={styles.info}>
          <Info label="TOTAL" value={numFiles || '0'} />
        </div>
        <div className={styles.info}>
          <Info label="NOT STARTED" value={notStartedFiles || '0'} />
        </div>
        <div className={styles.info}>
          <Info label="IN PROGRESS" value={numInProgressFiles || '0'} />
        </div>
        <div className={styles.info}>
          <Info label="COMPLETED" value={numCompletedFiles || '0'} />
        </div>
        <div className={styles.info}>
          <Info label="PARTIAL SUCCESS" value={partialSuccessFiles || '0'} />
        </div>
        <div className={styles.info}>
          <Info label="Error" value={numErrorFiles || '0'} />
        </div>
      </div>
      {loading ? <CenteredLoader absolute /> : null}
    </div>
  );
};
